<!-- Ambient Background Gradients -->
<div class="fixed inset-0 z-0 overflow-hidden pointer-events-none">
  <div class="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full blur-[120px]" style="background: rgba(167, 165, 255, 0.12)"></div>
  <div class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full blur-[100px]" style="background: rgba(83, 221, 252, 0.08)"></div>
</div>

<!-- Main Content Canvas -->
<div class="relative z-10 w-full min-h-screen flex items-center justify-center px-6">
  <div class="w-full max-w-xl">

    @if (result(); as r) {
      <!-- Import Result Card -->
      <div class="glass-panel p-10 rounded-xl relative overflow-hidden">
        <div class="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl" style="background: rgba(74, 222, 128, 0.1)"></div>

        <div class="relative z-10 text-center mb-10">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-6" style="background: var(--wb-bg-overlay)">
            <i class="pi pi-check-circle text-4xl text-wb-success"></i>
          </div>
          <h1 class="font-mono text-4xl font-bold text-wb-text tracking-tight mb-3">Import Complete</h1>
          <p class="text-wb-muted text-lg leading-relaxed max-w-sm mx-auto">
            Your project has been successfully imported and is ready to use.
          </p>
        </div>

        <div class="relative z-10 space-y-8">
          <!-- Stats Grid -->
          <div class="grid grid-cols-3 gap-4">
            <div class="p-4 rounded-xl" style="background: var(--wb-bg-surface)">
              <p class="m-0 text-xs text-wb-muted uppercase tracking-widest font-semibold mb-1">Project</p>
              <p class="m-0 text-sm font-bold text-wb-text">{{ r.projectSlug }}</p>
            </div>
            <div class="p-4 rounded-xl" style="background: var(--wb-bg-surface)">
              <p class="m-0 text-xs text-wb-muted uppercase tracking-widest font-semibold mb-1">Scenes</p>
              <p class="m-0 text-2xl font-bold font-mono text-wb-text">{{ r.scenesImported }}</p>
            </div>
            <div class="p-4 rounded-xl" style="background: var(--wb-bg-surface)">
              <p class="m-0 text-xs text-wb-muted uppercase tracking-widest font-semibold mb-1">Images</p>
              <p class="m-0 text-2xl font-bold font-mono text-wb-text">{{ r.imagesImported }}</p>
            </div>
          </div>

          @if (r.errors.length > 0) {
            <div class="p-4 rounded-xl" style="background: var(--wb-bg-surface); border-left: 3px solid var(--wb-warn)">
              <p class="m-0 text-sm font-semibold text-wb-warn mb-2">{{ r.errors.length }} warning(s)</p>
              <ul class="m-0 pl-4">
                @for (e of r.errors; track e) {
                  <li class="text-xs text-wb-secondary mb-1">{{ e }}</li>
                }
              </ul>
            </div>
          }

          <!-- Action Buttons -->
          <a
            [routerLink]="['/', companySlug, 'campaigns', campaignSlug, r.projectSlug, 'editor']"
            class="cta-button w-full py-5 rounded-full font-bold text-lg tracking-tight flex items-center justify-center gap-3 no-underline"
          >
            Open Project
            <i class="pi pi-arrow-right"></i>
          </a>

          <div class="flex items-center justify-center gap-6 pt-2">
            <button
              class="text-sm font-medium text-wb-muted hover:text-wb-text transition-colors flex items-center gap-2 bg-transparent border-0 cursor-pointer"
              (click)="goToProjects()"
            >
              <i class="pi pi-list text-sm"></i>
              All Projects
            </button>
          </div>
        </div>
      </div>
    } @else {
      <div class="glass-panel p-10 rounded-xl relative overflow-hidden">
        <div class="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl" style="background: rgba(167, 165, 255, 0.1)"></div>

        <!-- Mode Toggle -->
        <div class="relative z-10 flex justify-center gap-2 mb-8">
          <button type="button" class="mode-btn" [class.mode-btn--active]="mode() === 'import'" (click)="setMode('import')">
            <i class="pi pi-cloud-upload text-sm"></i> Import
          </button>
          <button type="button" class="mode-btn" [class.mode-btn--active]="mode() === 'batch'" (click)="setMode('batch')">
            <i class="pi pi-list text-sm"></i> Batch
          </button>
          <button type="button" class="mode-btn" [class.mode-btn--active]="mode() === 'create'" (click)="setMode('create')">
            <i class="pi pi-plus text-sm"></i> Create New
          </button>
          @if (isAdmin()) {
            <button type="button" class="mode-btn" [class.mode-btn--active]="mode() === 'catalog'" (click)="setMode('catalog')">
              <i class="pi pi-database text-sm"></i> Catalog
            </button>
          }
        </div>

        @if (mode() === 'import') {
          <div class="relative z-10 text-center mb-10">
            <h1 class="font-mono text-3xl font-bold text-wb-text tracking-tight mb-3">Import Project</h1>
            <p class="text-wb-muted text-base leading-relaxed max-w-sm mx-auto">Paste your project manifest URL below.</p>
          </div>
          <form class="space-y-8 relative z-10" (ngSubmit)="submit()">
            <div class="space-y-3">
              <label class="block text-sm font-medium text-wb-accent-2 tracking-wider uppercase ml-1" for="url">Project JSON URL</label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-wb-muted group-focus-within:text-wb-accent-2 transition-colors">
                  <i class="pi pi-link"></i>
                </div>
                <input id="url" type="url" pInputText [(ngModel)]="projectJsonUrl" name="projectJsonUrl" required class="create-input create-input--icon w-full" placeholder="https://..." />
              </div>
            </div>
            @if (error()) {
              <div class="flex items-center gap-3 p-4 rounded-xl text-sm" style="background: var(--wb-danger-subtle)">
                <i class="pi pi-exclamation-circle text-wb-danger text-lg"></i>
                <span class="text-wb-text">{{ error() }}</span>
              </div>
            }
            <button type="submit" class="cta-button w-full py-5 rounded-full font-bold text-lg tracking-tight flex items-center justify-center gap-3" [disabled]="loading()">
              @if (loading()) { <i class="pi pi-spin pi-spinner"></i> Importing... } @else { Import Project <i class="pi pi-arrow-right"></i> }
            </button>
          </form>
        } @else if (mode() === 'batch') {
          <div class="relative z-10 text-center mb-8">
            <h1 class="font-mono text-3xl font-bold text-wb-text tracking-tight mb-3">Batch Import</h1>
            <p class="text-wb-muted text-base leading-relaxed max-w-sm mx-auto">One project.json URL per line. Lines starting with <code>#</code> are skipped.</p>
          </div>
          <form class="space-y-5 relative z-10" (ngSubmit)="submitBatch()">
            <textarea
              pTextarea
              class="batch-textarea create-input w-full font-mono"
              rows="10"
              [ngModel]="batchText()"
              (ngModelChange)="batchText.set($event)"
              name="batchText"
              placeholder="https://.../project.json&#10;https://.../project.json"
              [disabled]="batchRunning()"
            ></textarea>

            @if (batchRows().length > 0) {
              <div class="batch-rows rounded-xl" style="background: var(--wb-bg-surface)">
                @for (row of batchRows(); track row.url) {
                  <div class="batch-row">
                    <i class="batch-row-icon pi"
                      [class.pi-clock]="row.status === 'pending'"
                      [class.pi-spin]="row.status === 'importing'"
                      [class.pi-spinner]="row.status === 'importing'"
                      [class.pi-check-circle]="row.status === 'done'"
                      [class.pi-exclamation-circle]="row.status === 'error'"
                      [class.text-wb-muted]="row.status === 'pending'"
                      [class.text-wb-accent-2]="row.status === 'importing'"
                      [class.text-wb-success]="row.status === 'done'"
                      [class.text-wb-danger]="row.status === 'error'"
                    ></i>
                    <span class="batch-row-url truncate flex-1" [title]="row.url">{{ row.url }}</span>
                    @if (row.status === 'done') {
                      <span class="batch-row-meta">{{ row.scenesImported }}sc / {{ row.imagesImported }}img</span>
                    } @else if (row.status === 'error') {
                      <span class="batch-row-meta text-wb-danger truncate" [title]="row.error">{{ row.error }}</span>
                    }
                  </div>
                }
              </div>
              <div class="text-center text-wb-muted text-sm">
                {{ batchSummary().done }} done · {{ batchSummary().failed }} failed · {{ batchSummary().total }} total
              </div>
            }

            @if (batchRunning()) {
              <button type="button" class="cta-button-secondary w-full py-4 rounded-full font-semibold tracking-tight flex items-center justify-center gap-2" (click)="cancelBatch()">
                <i class="pi pi-times"></i> Cancel
              </button>
            } @else {
              <button type="submit" class="cta-button w-full py-5 rounded-full font-bold text-lg tracking-tight flex items-center justify-center gap-3" [disabled]="!batchText().trim()">
                Import All <i class="pi pi-arrow-right"></i>
              </button>
            }
          </form>
        } @else if (mode() === 'create') {
          <div class="relative z-10 text-center mb-10">
            <h1 class="font-mono text-3xl font-bold text-wb-text tracking-tight mb-3">Create New Project</h1>
            <p class="text-wb-muted text-base leading-relaxed max-w-sm mx-auto">Set up a new project from scratch.</p>
          </div>
          <form class="space-y-6 relative z-10" (ngSubmit)="createProject()">
            <div class="space-y-3">
              <label class="block text-sm font-medium text-wb-accent-2 tracking-wider uppercase ml-1" for="name">Project name</label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-wb-muted group-focus-within:text-wb-accent-2 transition-colors">
                  <i class="pi pi-tag"></i>
                </div>
                <input #nameInput id="name" pInputText class="create-input create-input--icon w-full" [ngModel]="newName" (ngModelChange)="onNameChange($event)" name="name" required placeholder="My Project" />
              </div>
            </div>
            <div class="space-y-3">
              <label class="block text-sm font-medium text-wb-muted tracking-wider uppercase ml-1" for="slug">Slug</label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-wb-muted group-focus-within:text-wb-accent-2 transition-colors">
                  <i class="pi pi-hashtag"></i>
                </div>
                <input id="slug" pInputText class="create-input create-input--icon w-full font-mono" [(ngModel)]="newSlug" name="slug" placeholder="my-project" />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2">
                <label class="block text-xs font-medium text-wb-muted uppercase ml-1">Aspect Ratio</label>
                <p-select [options]="aspectOptions" [ngModel]="selectedAspect()" (ngModelChange)="onAspectChange($event)" optionLabel="label" optionValue="value" name="aspect" styleClass="w-full create-input" appendTo="body" />
              </div>
              <div class="space-y-2">
                <label class="block text-xs font-medium text-wb-muted uppercase ml-1">FPS</label>
                <p-inputnumber [(ngModel)]="newFps" name="fps" [showButtons]="false" styleClass="p-inputnumber-fluid create-input" />
              </div>
            </div>
            @if (selectedAspect() === 'custom') {
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label class="block text-xs font-medium text-wb-muted uppercase ml-1">Width</label>
                  <p-inputnumber [(ngModel)]="newWidth" name="width" [showButtons]="false" styleClass="p-inputnumber-fluid create-input" />
                </div>
                <div class="space-y-2">
                  <label class="block text-xs font-medium text-wb-muted uppercase ml-1">Height</label>
                  <p-inputnumber [(ngModel)]="newHeight" name="height" [showButtons]="false" styleClass="p-inputnumber-fluid create-input" />
                </div>
              </div>
            }
            @if (createError()) {
              <div class="flex items-center gap-3 p-4 rounded-xl text-sm" style="background: var(--wb-danger-subtle)">
                <i class="pi pi-exclamation-circle text-wb-danger text-lg"></i>
                <span class="text-wb-text">{{ createError() }}</span>
              </div>
            }
            <button type="submit" class="cta-button w-full py-5 rounded-full font-bold text-lg tracking-tight flex items-center justify-center gap-3" [disabled]="creating()">
              @if (creating()) { <i class="pi pi-spin pi-spinner"></i> Creating... } @else { Create Project <i class="pi pi-arrow-right"></i> }
            </button>
          </form>
        } @else if (mode() === 'catalog') {
          <div class="relative z-10 text-center mb-8">
            <h1 class="font-mono text-3xl font-bold text-wb-text tracking-tight mb-3">Import Catalog</h1>
            <p class="text-wb-muted text-base leading-relaxed max-w-sm mx-auto">
              Pick a project from the global catalog — media copies fresh from S3 into this project.
            </p>
          </div>

          @if (catalogLoading()) {
            <div class="relative z-10 flex items-center justify-center gap-3 py-10 text-wb-muted text-sm">
              <i class="pi pi-spin pi-spinner"></i> Loading catalog…
            </div>
          } @else if (catalogLoadError()) {
            <div class="relative z-10 flex items-center gap-3 p-4 rounded-xl text-sm" style="background: var(--wb-danger-subtle)">
              <i class="pi pi-exclamation-circle text-wb-danger text-lg"></i>
              <span class="text-wb-text">{{ catalogLoadError() }}</span>
              <button type="button" pButton class="p-button-sm p-button-text ml-auto" (click)="loadCatalogEntries()">Retry</button>
            </div>
          } @else if (catalogEntries().length === 0) {
            <p class="relative z-10 text-center text-wb-muted text-sm py-10">
              No ready entries yet — add project.json links in <a routerLink="/admin/import-catalog">Admin → Import Catalog</a>.
            </p>
          } @else {
            <div class="relative z-10 import-repo-list rounded-xl" style="background: var(--wb-bg-surface)">
              @for (row of catalogEntries(); track row._id) {
                <div class="import-repo-row">
                  <div class="import-repo-row-main">
                    <div class="import-repo-row-name">
                      <i class="pi"
                        [class.pi-database]="!row.importing && row.run?.status !== 'running'"
                        [class.pi-spin]="row.importing || row.run?.status === 'running'"
                        [class.pi-spinner]="row.importing || row.run?.status === 'running'"
                        [class.pi-check]="row.run?.status === 'done'"
                        [class.pi-times]="row.run?.status === 'error'"
                        [class.text-wb-muted]="!row.run"
                        [class.text-wb-accent-2]="row.importing || row.run?.status === 'running'"
                        [class.text-wb-success]="row.run?.status === 'done'"
                        [class.text-wb-danger]="row.run?.status === 'error'"
                      ></i>
                      <span class="truncate">{{ row.name }}</span>
                    </div>
                    <div class="import-repo-row-actions">
                      @if (row.manifest) {
                        <span class="text-wb-muted text-xs">{{ importableFileCount(row) }} ({{ row.manifest.fileCount }}) · {{ formatBytes(row.manifest.totalSize) }}</span>
                      }
                      @if (row.run?.status === 'done' && row.projectId) {
                        <a [routerLink]="catalogEditorLink(row)" pButton [text]="true" class="import-icon-btn" title="Open in editor">
                          <i class="pi pi-arrow-up-right"></i>
                        </a>
                      }
                      <button type="button" pButton class="p-button-sm"
                        [disabled]="row.importing || row.run?.status === 'running'"
                        (click)="importCatalogEntry(row)"
                      >Import</button>
                    </div>
                  </div>

                  @if (row.error) {
                    <div class="import-repo-row-error text-wb-danger">{{ row.error }}</div>
                  }

                  @if (row.importing || row.run) {
                    <div class="import-checklist">
                      @for (step of catalogSteps(row); track step.key) {
                        <div class="import-checklist-step">
                          <i class="pi"
                            [class.pi-circle]="step.status === 'pending'"
                            [class.pi-spin]="step.status === 'active'"
                            [class.pi-spinner]="step.status === 'active'"
                            [class.pi-check]="step.status === 'done'"
                            [class.pi-times]="step.status === 'error'"
                            [class.text-wb-muted]="step.status === 'pending'"
                            [class.text-wb-accent-2]="step.status === 'active'"
                            [class.text-wb-success]="step.status === 'done'"
                            [class.text-wb-danger]="step.status === 'error'"
                          ></i>
                          <span class="import-checklist-step-label" [class.text-wb-muted]="step.status === 'pending'">{{ step.label }}</span>
                          @if (step.trailing) {
                            <span class="import-checklist-step-trailing font-mono">{{ step.trailing }}</span>
                          }
                        </div>
                      }
                      @if (row.run; as run) {
                        <div class="import-checklist-step">
                          <span class="import-checklist-step-label text-wb-muted">
                            {{ run.copiedFiles }}/{{ run.totalFiles }} files
                            @if ((run.totalBytes ?? 0) > 0) {
                              · {{ formatBytes(run.bytesCopied ?? 0) }} / {{ formatBytes(run.totalBytes ?? 0) }}
                            }
                            @if (run.failedFiles > 0) {
                              · {{ run.failedFiles }} failed
                            }
                          </span>
                        </div>
                        @if (run.failedTasks.length; as failedCount) {
                          <div class="import-checklist-step text-wb-danger import-checklist-retry-row">
                            <i class="pi pi-exclamation-triangle"></i>
                            <span class="import-checklist-step-label">{{ failedCount }} file(s) failed to copy</span>
                            <button class="import-retry-btn" [disabled]="row.retryingFailedCopies" (click)="retryFailedCatalogCopies(row)">
                              @if (row.retryingFailedCopies) { Retrying... } @else { Retry }
                            </button>
                          </div>
                        }
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        }

        <div class="flex items-center justify-center pt-6 relative z-10">
          <button type="button" class="text-sm font-medium text-wb-muted hover:text-wb-text transition-colors flex items-center gap-2 bg-transparent border-0 cursor-pointer" (click)="cancel()">
            <i class="pi pi-times text-sm"></i> Cancel
          </button>
        </div>
      </div>
    }
  </div>
</div>

<!-- Decorative Bottom Gradient -->
<div class="fixed bottom-0 left-0 right-0 h-32 pointer-events-none" style="background: linear-gradient(to top, var(--wb-bg), transparent)"></div>
